// tslint:disable:jsx-boolean-value
import React from 'react';
import styled from 'styled-components';
import Icon from 'components/ui/Icon';
import TranslatedText from 'components/i18n/TranslatedText';
import IconLabel from 'components/ui/IconLabel';
import ChannelLiveBadge from 'components/ui/ChannelLiveBadge';
import withTooltip, { PositionKey } from 'components/core/withTooltip';
import { PageType } from 'models/IObject';
import {
  ADMIN_TEXT_300,
  ADMIN_TEXT_100,
  ADMIN_ALERT_WARNING,
} from 'style/constants';
import { SINGLE_LINE_ELLIPSIS, CIRCLE } from 'style/mixins';
import { ADMIN_TEXT_LABEL_M_MEDIUM, ADMIN_TEXT_LABEL_XS_BOLD } from 'style/design-system/textStyles';
import { useSelector } from 'react-redux';
import { getAdminSurface1, getAdminText100 } from 'services/themes';
import type { TranslationKey } from 'hooks/use-translation';

export interface PageInfoProps {
  className?: string;
  hasChanges: boolean;
  image?: string | null;
  isHome: boolean;
  name: string;
  pageId: string;
  tooltipPosition?: PositionKey;
  type: PageType;
}

export default function({
  className,
  hasChanges,
  image,
  isHome,
  name,
  pageId,
  tooltipPosition = 'top',
  type,
}: PageInfoProps) {
  const adminSurface1 = useSelector(getAdminSurface1);
  const adminText100 = useSelector(getAdminText100);

  return (
    <PageInfo className={className}>
      <PageImage src={image}>
        {!image && <PageDefaultImage />}
        {isHome && (
          <IconLabel
            background={adminSurface1}
            icon="homeFilled"
            iconColor={adminText100}
            iconHeight="9.13px"
            iconWidth="10.3px"
          />
        )}
      </PageImage>
      <TextWrapper>
        <TypeWrapper>
          {hasChanges && <ChangesIndicator tooltipPosition={tooltipPosition} />}
          <TranslatedText
            component={Type}
            stringKey={PAGE_TYPE_KEY[type]}
          />
          <ChannelLiveBadge id={pageId} />
        </TypeWrapper>
        <Name>{name}</Name>
      </TextWrapper>
    </PageInfo>
  );
}

const PAGE_TYPE_KEY: { [K in PageType]: TranslationKey } = {
  [PageType.CHANNEL]: 'ADMIN_LABEL_CHANNEL',
  [PageType.LANDING]: 'ADMIN_LABEL_PAGE',
} as const;

const PageInfo = styled.div`
  display: flex;
  height: 30px;
  gap: 15px;
  padding-right: 10px;
  min-width: 0;
  z-index: 2;
  pointer-events: none;
`;

const PageImage = styled.div<{src?: string | null}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 55px;
  height: 100%;
  ${({ src }) => `background: center / cover no-repeat url("${src}");`}
  object-fit: cover;
  border-radius: 4.5px;
  position: relative;
  flex-shrink: 0;
  flex-grow: 0;
  & > *:last-child {
    z-index: 1;
  }
`;

const PageDefaultImage = styled(Icon).attrs({ name: 'defaultChannel' })`
  position: absolute;
  &, & > svg {
    width: 100%;
    height: 100%;
    border-radius: 4.5px;
  }
`;

const TextWrapper = styled.label`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0;
`;

const Type = styled.span`
${ADMIN_TEXT_LABEL_XS_BOLD}
  color: ${ADMIN_TEXT_300};
  text-transform: uppercase;
`;

const TypeWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
`;

const Name = styled.span`
  ${ADMIN_TEXT_LABEL_M_MEDIUM}
  color: ${ADMIN_TEXT_100};
  min-height: 22px;
  ${SINGLE_LINE_ELLIPSIS}
`;

const ChangesIndicator = withTooltip(styled.div.attrs({ radius: '6px' })`
  ${CIRCLE}
  background: ${ADMIN_ALERT_WARNING};
  pointer-events: all;
`, {
  admin: true,
  tooltipAlign: 'right',
  tooltipArrowCss: 'margin-left: 12px;',
  tooltipCss: `
    margin-left: -12px;
    padding: 3px 8px;
  `,
  tooltipPosition: 'top',
  tooltipKey: 'ADMIN_LABEL_CHANGES_SAVED_PUBLISH',
});
